import { Skeleton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Grid, Box } from '@mui/material';
import React from 'react';
import {
	CerebroBreadcrumb,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroPageContainer,
} from '../../shared/components';
import { LenderMilestoneDetails } from '../../shared/components/Layout/LenderMilestoneDetails';
import { useAuth } from '../../shared/context';
import { useDataRoomMilestoneQuery, useDataRoomQuery, useDataRoomTeamMembersQuery } from '../../shared/hooks';
import { AssociatedTeamMember, DataRoomUser, MilestoneReport, UnlockUser } from '../../shared/models';
import { AddLenderNoteForm } from '../Participants/Lenders/AddLenderNoteForm';
import { AddTeamMemberForm } from '../Participants/Lenders/AddTeamMemberForm';
import { DeleteDataRoomTeamMemberDialog } from '../Participants/Lenders/DeleteDataRoomTeamMemberDialog';
import { LenderDeleteDialog } from '../Participants/Lenders/LenderDeleteDialog';
import { LenderGrantAccessDialog } from '../Participants/Lenders/LenderGrantAccessDialog';
import { OptOutDetails } from '../Participants/Lenders/OptOutDetails';
import { OptOutForm } from '../Participants/Lenders/OptOutForm';
import { SendEmailForm } from '../Participants/Lenders/SendEmailForm';
import { SendInviteForm } from '../Participants/Lenders/SendInviteForm';
import { UploadNdaForm } from '../Participants/Lenders/UploadNdaForm';
import { ViewContactDetails } from '../Participants/Lenders/ViewContactDetails';
import { ViewLenderNotes } from '../Participants/Lenders/ViewLenderNotes';
import { ViewSentEmails } from '../Participants/Lenders/ViewSentEmails';
import { UnlockUserDialog } from '../Participants/UnlockUserDialog';
import { ViewAccessLogs } from '../Participants/ViewAccessLogs';
import { MilestoneRow } from './MilestoneRow';
import { SendLenderEmailsForm } from './SendLenderEmailsForm';
import { EmailLendersActionButton } from './EmailLendersActionButton';

interface MilestonePageProps {
	dataRoomGuid: string | undefined;
}

export const MilestonePage: React.FC<MilestonePageProps> = ({ dataRoomGuid }) => {
	const { currentUser } = useAuth();
	const dataRoomQuery = useDataRoomQuery(dataRoomGuid);
	const milestoneQuery = useDataRoomMilestoneQuery(dataRoomGuid);
	const teamMembersQuery = useDataRoomTeamMembersQuery(dataRoomGuid);
	const [milestoneDetail, setMilestoneDetail] = React.useState<MilestoneReport | null>(null);
	const isLoading = milestoneQuery.isLoading || teamMembersQuery.isLoading || dataRoomQuery.isLoading;
	const isFetching =
		milestoneQuery.isRefetching ||
		milestoneQuery.isFetching ||
		teamMembersQuery.isRefetching ||
		teamMembersQuery.isFetching ||
		dataRoomQuery.isRefetching ||
		dataRoomQuery.isFetching ||
		isLoading;
	const isSuccess = milestoneQuery.isSuccess && teamMembersQuery.isSuccess && dataRoomQuery.isSuccess;

	const [userBeingOptedOut, setUserBeingOptedOut] = React.useState<DataRoomUser | null>(null);
	const [viewUserOptedOutDetails, setViewUserOptedOutDetails] = React.useState<DataRoomUser | null>(null);
	const [userBeingUnlocked, setUserBeingUnlocked] = React.useState<UnlockUser | null>(null);
	const [viewAccessLogs, setViewAccessLogs] = React.useState<UnlockUser | null>(null);
	const [notesBeingViewed, setNotesBeingViewed] = React.useState<DataRoomUser | null>(null);
	const [noteBeingAdded, setNoteBeingAdded] = React.useState<DataRoomUser | null>(null);
	const [teamMemberBeingAdded, setTeamMemberBeingAdded] = React.useState<DataRoomUser | null>(null);
	const [userBeingInvited, setUserBeingInvited] = React.useState<UnlockUser | null>(null);
	const [sendEmail, setSendEmail] = React.useState<DataRoomUser | null>(null);
	const [viewEmails, setViewEmails] = React.useState<DataRoomUser | null>(null);
	const [userUploadedNda, setUserUploadingNda] = React.useState<DataRoomUser | null>(null);
	const [userBeingGranted, setUserBeingGranted] = React.useState<DataRoomUser | null>(null);
	const [dataRoomUserBeingDeleted, setDataRoomUserBeingDeleted] = React.useState<DataRoomUser | null>(null);

	const [teamMemberBeingRemoved, setTeamMemberBeingRemoved] = React.useState<AssociatedTeamMember | null>(null);
	const [viewContactDetails, setViewContactDetails] = React.useState<DataRoomUser | null>(null);
	const [viewTeamMemberDetails, setViewTeamMemberDetails] = React.useState<AssociatedTeamMember | null>(null);
	const [sendLenderEmailsOpen, setSendLenderEmailsOpen] = React.useState<boolean>(false);

	const [filter, setFilter] = React.useState<string>('active');

	const onChangeFilter = (target: string) => {
		setFilter(target);
	};

	const clearDialogsAndDrawers = (): void => {
		setMilestoneDetail(null);
		setUserBeingOptedOut(null);
		setUserBeingUnlocked(null);
		setViewAccessLogs(null);
		setNotesBeingViewed(null);
		setNoteBeingAdded(null);
		setUserBeingInvited(null);
		setSendEmail(null);
		setViewEmails(null);
		setUserUploadingNda(null);
		setTeamMemberBeingAdded(null);
		setTeamMemberBeingRemoved(null);
		setUserBeingGranted(null);
		setDataRoomUserBeingDeleted(null);
		setViewUserOptedOutDetails(null);
		setViewContactDetails(null);
		setViewTeamMemberDetails(null);
		setSendLenderEmailsOpen(false);
	};

	return (
		<CerebroPageContainer maxWidth="100%">
			<CerebroBreadcrumb>Lender Progress</CerebroBreadcrumb>

			<Box sx={{ marginBottom: 2 }}>
				<CerebroDropdown
					sx={{ marginRight: 1, width: '15%' }}
					value={filter}
					onChange={data => onChangeFilter(data.target.value as string)}
				>
					<CerebroDropdownMenuItem value="all">All Lenders</CerebroDropdownMenuItem>
					<CerebroDropdownMenuItem value="active">Active Lenders</CerebroDropdownMenuItem>
					<CerebroDropdownMenuItem value="inactive">Inactive Lenders</CerebroDropdownMenuItem>
				</CerebroDropdown>
				<EmailLendersActionButton
					setAll={() => {
						setFilter('all');
						setSendLenderEmailsOpen(true);
					}}
					setActive={() => {
						setFilter('active');
						setSendLenderEmailsOpen(true);
					}}
					setInactive={() => {
						setFilter('inactive');
						setSendLenderEmailsOpen(true);
					}}
				/>
			</Box>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: '5px', padding: '0px' }}></TableCell>
							<TableCell></TableCell>
							<TableCell>Lender Name</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Teaser</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Access Deal</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Documents Downloaded</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Ready for Call</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Term Sheet Submitted</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Underwriting</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Funded</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<>
								<TableRow>
									<TableCell colSpan={12}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={12}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={12}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
							</>
						)}
						{isSuccess && (
							<>
								{milestoneQuery.data
									.sort((a, b) => a.bankName.localeCompare(b.bankName))
									.filter(
										item =>
											filter == 'all' ||
											(filter == 'active' && item.activeUser == true) ||
											(filter == 'inactive' && item.activeUser == false)
									)
									.map(milestone => {
										const teamMembers = teamMembersQuery.data.filter(
											item =>
												item.bankContactGuid == milestone.bankContactGuid ||
												item.parentDataRoomUserGuid == milestone.guid
										);

										return (
											<MilestoneRow
												key={milestone.guid}
												milestone={milestone}
												teamMembers={teamMembers}
												dataRoom={dataRoomQuery.data}
												onDetails={setMilestoneDetail}
												unlockUser={setUserBeingUnlocked}
												accessLogs={setViewAccessLogs}
												optOutUser={setUserBeingOptedOut}
												addNote={setNoteBeingAdded}
												viewNotes={setNotesBeingViewed}
												addTeamMember={setTeamMemberBeingAdded}
												removeTeamMember={setTeamMemberBeingRemoved}
												sendInvite={setUserBeingInvited}
												sendEmail={setSendEmail}
												viewEmails={setViewEmails}
												uploadNda={setUserUploadingNda}
												grantAccess={setUserBeingGranted}
												deleteDataRoomUser={setDataRoomUserBeingDeleted}
												viewOptOut={setViewUserOptedOutDetails}
												contactDetails={setViewContactDetails}
												teamMemberDetails={setViewTeamMemberDetails}
											/>
										);
									})}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<LenderMilestoneDetails report={milestoneDetail} close={clearDialogsAndDrawers} />

			<SendLenderEmailsForm
				dataRoomGuid={dataRoomGuid}
				open={sendLenderEmailsOpen}
				milestones={milestoneQuery.data ?? []}
				statusFilter={filter as 'active' | 'inactive' | 'all'}
				close={clearDialogsAndDrawers}
			/>
			<OptOutForm dataRoomGuid={dataRoomGuid} user={userBeingOptedOut} close={clearDialogsAndDrawers} />
			<OptOutDetails dataRoomGuid={dataRoomGuid} user={viewUserOptedOutDetails} close={clearDialogsAndDrawers} />
			<UnlockUserDialog dataRoomGuid={dataRoomGuid} user={userBeingUnlocked} close={clearDialogsAndDrawers} />
			<ViewAccessLogs dataRoomGuid={dataRoomGuid} user={viewAccessLogs} close={clearDialogsAndDrawers} />
			<ViewLenderNotes dataRoomGuid={dataRoomGuid} user={notesBeingViewed} close={clearDialogsAndDrawers} />
			<AddTeamMemberForm
				dataRoomGuid={dataRoomGuid}
				user={teamMemberBeingAdded}
				isBankUser={currentUser.isBankUser}
				close={clearDialogsAndDrawers}
			/>
			<SendInviteForm dataRoomGuid={dataRoomGuid} user={userBeingInvited} close={clearDialogsAndDrawers} />
			<SendEmailForm dataRoomGuid={dataRoomGuid} user={sendEmail} close={clearDialogsAndDrawers} />
			<ViewSentEmails dataRoomGuid={dataRoomGuid} user={viewEmails} close={clearDialogsAndDrawers} />
			<UploadNdaForm dataRoomGuid={dataRoomGuid} user={userUploadedNda} close={clearDialogsAndDrawers} />
			<LenderGrantAccessDialog
				dataRoomGuid={dataRoomGuid}
				user={userBeingGranted}
				close={clearDialogsAndDrawers}
			/>
			<LenderDeleteDialog
				dataRoomGuid={dataRoomGuid}
				user={dataRoomUserBeingDeleted}
				close={clearDialogsAndDrawers}
			/>

			<ViewContactDetails roomGuid={dataRoomGuid} roomUser={viewContactDetails} close={clearDialogsAndDrawers} />
			<ViewContactDetails
				roomGuid={dataRoomGuid}
				teamMember={viewTeamMemberDetails}
				close={clearDialogsAndDrawers}
			/>

			<AddLenderNoteForm
				dataRoomGuid={dataRoomGuid}
				user={noteBeingAdded}
				currentUser={currentUser}
				close={clearDialogsAndDrawers}
			/>

			<DeleteDataRoomTeamMemberDialog
				dataRoomGuid={dataRoomGuid}
				member={teamMemberBeingRemoved}
				close={clearDialogsAndDrawers}
			/>
		</CerebroPageContainer>
	);
};
